// Multi-tab editor manager. Owns the in-memory tab list and drives
// the singleton EditorView; switching tabs is a `view.setState` swap
// so each tab keeps its own undo history, selection, and scroll
// position. Persists the open tabs + active id to localStorage so
// reload restores the workspace (source only — undo history isn't
// serializable across reloads).
//
// Usage:
//
//   const tabs = new TabManager({ view, barEl, buildState, ... });
//   if (!tabs.restore()) {
//     tabs.openOrFocus('scratch', 'untitled.sas', 'scratch', placeholder);
//   }
//   tabs.openOrFocus('file:'+path, basename(path), 'file', sourceText);
//   tabs.openOrFocus('lesson', lesson.title, 'lesson', lesson.starter, true);

import type { EditorState } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';

const PERSIST_KEY = 'kolilang:tabs';
// Pre-rename key — migrated lazily on restore() so existing users
// keep their open tabs.
const LEGACY_PERSIST_KEY = 'sassy:tabs';

export type TabOrigin = 'scratch' | 'file' | 'lesson';

export interface TabInfo {
  id: string;
  title: string;
  origin: TabOrigin;
}

export interface TabManagerOptions {
  view: EditorView;
  barEl: HTMLElement;
  /// Build a fresh EditorState carrying every extension the editor
  /// needs (language, linter, keymap, theme, listeners). Called once
  /// per new tab + once per restored tab on reload.
  buildState: (source: string) => EditorState;
  /// Called whenever the active tab changes (or initially activates),
  /// so callers can update the status-bar crumbs / file-tree
  /// highlight / etc.
  onActiveChange?: (tab: TabInfo | null) => void;
  /// Called after `view.setState(...)` so callers can re-apply any
  /// per-state side-effects (theme compartment reconfigure, etc.).
  /// The new state's compartment values reflect whatever theme was
  /// current when buildState ran; if the page theme has changed
  /// since, this callback bumps the active state in line.
  onActivated?: () => void;
  /// Called when the user double-clicks a tab's title, so main.ts can
  /// run the origin-aware rename flow (scratch → in-place title;
  /// file → Save As copy; lesson → no-op).
  onRenameRequest?: (id: string) => void;
  /// Called after a tab is closed so callers can drop per-tab side
  /// state (e.g. stashed file handles) and avoid leaks.
  onClose?: (id: string) => void;
}

interface InternalTab {
  info: TabInfo;
  state: EditorState;
  /// Source as last persisted/loaded; used to compute the dirty dot.
  savedSource: string;
}

export class TabManager {
  private opts: TabManagerOptions;
  private tabs: Map<string, InternalTab> = new Map();
  private order: string[] = [];
  private activeId: string | null = null;

  constructor(opts: TabManagerOptions) {
    this.opts = opts;
  }

  /// Open a tab if it doesn't exist; focus it if it does. With
  /// `replaceContent: true`, an existing tab's source is replaced
  /// (used by the tutorial: clicking a different lesson reloads the
  /// "lesson" tab rather than stacking).
  openOrFocus(
    id: string,
    title: string,
    origin: TabOrigin,
    source: string,
    replaceContent = false,
  ): void {
    const existing = this.tabs.get(id);
    if (existing) {
      existing.info = { id, title, origin };
      if (replaceContent) {
        existing.state = this.opts.buildState(source);
        existing.savedSource = source;
        // If we're replacing the active tab's content, also push the
        // new state through the view immediately.
        if (this.activeId === id) {
          this.opts.view.setState(existing.state);
          this.opts.onActivated?.();
        }
      }
      this.activate(id);
      this.render();
      this.persist();
      return;
    }

    const tab: InternalTab = {
      info: { id, title, origin },
      state: this.opts.buildState(source),
      savedSource: source,
    };
    this.tabs.set(id, tab);
    this.order.push(id);
    this.activate(id);
    this.render();
    this.persist();
  }

  close(id: string): void {
    if (!this.tabs.has(id)) return;
    if (this.isDirty(id)) {
      const t = this.tabs.get(id)!;
      const ok = window.confirm(
        `“${t.info.title}” has unsaved changes. Close anyway?`,
      );
      if (!ok) return;
    }
    const idx = this.order.indexOf(id);
    this.tabs.delete(id);
    if (idx >= 0) this.order.splice(idx, 1);
    // Let main.ts drop per-tab side state (stashed file handles) before
    // any neighbour activation re-renders.
    this.opts.onClose?.(id);

    if (this.activeId === id) {
      this.activeId = null;
      // Pick the neighbour: prefer right (now at idx), fall back left.
      const next = this.order[idx] ?? this.order[idx - 1];
      if (next) {
        this.activate(next);
      } else {
        // No tabs left — open a fresh scratch so the editor isn't empty.
        this.openOrFocus('scratch', 'untitled.sas', 'scratch', '');
        return;
      }
    }
    this.render();
    this.persist();
  }

  /// Clear the dirty marker for `id` after a successful save. The
  /// caller passes the just-written source so future edits compare
  /// against it, not the pre-save baseline.
  markSaved(id: string, savedSource: string): void {
    const t = this.tabs.get(id);
    if (!t) return;
    t.savedSource = savedSource;
    this.render();
    this.persist();
  }

  /// Move a tab to a new id (and optionally a new title) atomically:
  /// updates the Map key, the order array, the active pointer, then
  /// fires onActiveChange so listeners (file-tree highlight, etc.)
  /// pick up the new id. Used when an `untitled:N` tab is saved into
  /// the picked folder and morphs into `file:<rel-path>`.
  rename(oldId: string, newId: string, title?: string): void {
    if (oldId === newId) return;
    const t = this.tabs.get(oldId);
    if (!t) return;
    if (this.tabs.has(newId)) {
      // Collision — caller should have closed the existing tab first.
      throw new Error(`tab id collision on rename: ${newId}`);
    }
    this.tabs.delete(oldId);
    if (title !== undefined) t.info = { ...t.info, title };
    t.info = { ...t.info, id: newId };
    this.tabs.set(newId, t);
    const idx = this.order.indexOf(oldId);
    if (idx >= 0) this.order[idx] = newId;
    if (this.activeId === oldId) this.activeId = newId;
    this.render();
    this.persist();
    // Tell consumers (file-tree highlight, tutorial rail) about the
    // new id so they can re-sync.
    this.opts.onActiveChange?.(t.info);
  }

  /// Rename a tab's display title in place, keeping its id and origin.
  /// Used for scratch-tab renames where the id stays `scratch` /
  /// `untitled:N` but the title (and thus the next Save suggestion)
  /// changes. Deliberately does NOT touch `savedSource`, so the dirty
  /// dot stays accurate — a rename isn't a save. `rename()` can't cover
  /// this because it early-returns when oldId === newId.
  setTitle(id: string, title: string): void {
    const t = this.tabs.get(id);
    if (!t) return;
    t.info = { ...t.info, title };
    this.render();
    this.persist();
    // Refresh dependents (status crumbs) for the active tab.
    if (this.activeId === id) this.opts.onActiveChange?.(t.info);
  }

  /// True if any tab has unsaved changes. Used by the beforeunload
  /// handler to warn before reload / close.
  anyDirty(): boolean {
    for (const id of this.tabs.keys()) {
      if (this.isDirty(id)) return true;
    }
    return false;
  }

  /// Snapshot of all open tab infos in display order. Used by
  /// main.ts to scan for untitled IDs and to walk dirty tabs on
  /// folder-source swaps.
  list(): TabInfo[] {
    return this.order
      .map((id) => this.tabs.get(id))
      .filter((t): t is InternalTab => t !== undefined)
      .map((t) => t.info);
  }

  /// Live source for a tab — reads the editor doc for the active tab,
  /// the snapshotted state for others. Returns null if no such tab.
  source(id: string): string | null {
    const t = this.tabs.get(id);
    if (!t) return null;
    return this.activeId === id
      ? this.opts.view.state.doc.toString()
      : t.state.doc.toString();
  }

  activate(id: string): void {
    if (!this.tabs.has(id)) return;
    if (this.activeId === id) {
      // Not switching, just refresh rendering (caller may have
      // updated the title).
      this.render();
      return;
    }

    // Snapshot the outgoing tab's current EditorState so its undo
    // history / selection / scroll come back when the user returns.
    if (this.activeId) {
      const cur = this.tabs.get(this.activeId);
      if (cur) cur.state = this.opts.view.state;
    }

    const tab = this.tabs.get(id)!;
    this.activeId = id;
    this.opts.view.setState(tab.state);
    this.opts.onActivated?.();
    this.opts.onActiveChange?.(tab.info);
    this.render();
    this.persist();
  }

  active(): TabInfo | null {
    if (!this.activeId) return null;
    const t = this.tabs.get(this.activeId);
    return t ? t.info : null;
  }

  isDirty(id: string): boolean {
    const t = this.tabs.get(id);
    if (!t) return false;
    const current = this.activeId === id
      ? this.opts.view.state.doc.toString()
      : t.state.doc.toString();
    return current !== t.savedSource;
  }

  /// Editor's docChanged listener calls this so the dirty dot
  /// updates as the user types in the active tab.
  notifyDocChanged(): void {
    this.render();
    // Persist on every doc change so reload doesn't lose typing.
    // localStorage writes are synchronous but cheap; if this turns
    // out to be a hotspot we'd debounce.
    this.persist();
  }

  render(): void {
    const bar = this.opts.barEl;
    bar.innerHTML = '';
    for (const id of this.order) {
      const t = this.tabs.get(id);
      if (!t) continue;
      const el = document.createElement('div');
      el.className = 'tab' + (id === this.activeId ? ' tab-active' : '');
      el.dataset.id = id;
      el.title = t.info.title;
      el.tabIndex = 0;

      if (this.isDirty(id)) {
        const dot = document.createElement('span');
        dot.className = 'tab-dirty';
        dot.title = 'Unsaved changes';
        el.appendChild(dot);
      }

      const titleSpan = document.createElement('span');
      titleSpan.className = 'tab-title';
      titleSpan.textContent = t.info.title;
      // Double-click the title to rename. stopPropagation keeps the
      // tab's own click (activate) from firing on the second click.
      titleSpan.addEventListener('dblclick', (ev) => {
        ev.stopPropagation();
        this.opts.onRenameRequest?.(id);
      });
      el.appendChild(titleSpan);

      const close = document.createElement('span');
      close.className = 'tab-close';
      close.textContent = '×';
      close.title = 'Close tab';
      close.addEventListener('click', (ev) => {
        ev.stopPropagation();
        this.close(id);
      });
      el.appendChild(close);

      el.addEventListener('click', () => this.activate(id));
      el.addEventListener('mousedown', (ev) => {
        // Middle-click also closes — VSCode/browser convention.
        if (ev.button === 1) {
          ev.preventDefault();
          this.close(id);
        }
      });
      el.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          this.activate(id);
        }
      });

      bar.appendChild(el);
    }
  }

  persist(): void {
    const snapshot = {
      tabs: this.order.map((id) => {
        const t = this.tabs.get(id)!;
        const source = this.activeId === id
          ? this.opts.view.state.doc.toString()
          : t.state.doc.toString();
        return { id, title: t.info.title, origin: t.info.origin, source };
      }),
      activeId: this.activeId,
    };
    try {
      localStorage.setItem(PERSIST_KEY, JSON.stringify(snapshot));
    } catch {
      // localStorage unavailable / quota; persistence is best-effort.
    }
  }

  /// Restore from localStorage. Returns true if any tabs were
  /// restored, false on empty / corrupt / missing state.
  restore(): boolean {
    let raw: string | null;
    try {
      raw = localStorage.getItem(PERSIST_KEY);
      if (raw === null) {
        const legacy = localStorage.getItem(LEGACY_PERSIST_KEY);
        if (legacy !== null) {
          localStorage.setItem(PERSIST_KEY, legacy);
          localStorage.removeItem(LEGACY_PERSIST_KEY);
          raw = legacy;
        }
      }
    } catch {
      return false;
    }
    if (!raw) return false;
    try {
      const data = JSON.parse(raw) as {
        tabs: { id: string; title: string; origin: TabOrigin; source: string }[];
        activeId: string | null;
      };
      if (!Array.isArray(data.tabs) || data.tabs.length === 0) return false;
      for (const t of data.tabs) {
        // Insert without auto-activate; we'll activate the saved one
        // at the end so the restore order matches the persisted order.
        const tab: InternalTab = {
          info: { id: t.id, title: t.title, origin: t.origin },
          state: this.opts.buildState(t.source),
          savedSource: t.source,
        };
        this.tabs.set(t.id, tab);
        this.order.push(t.id);
      }
      const activeId = data.activeId && this.tabs.has(data.activeId)
        ? data.activeId
        : this.order[0];
      this.activeId = null; // force activate to actually swap
      this.activate(activeId);
      return true;
    } catch {
      return false;
    }
  }
}
